import {
  parseSmartPhrases,
  isPhraseSegment,
  type SmartPhraseContext,
  type SmartPhraseKey,
  interpolateSmartPhrasesWithResolvedPhrases,
} from '@rsos/utils/SmartPhrases';
import { type ActiveCapability } from '../capstone/psapInfo/psapInfo.types';
import { type PreconfiguredResponse } from './preconfiguredMessagesAPI';
import { type DefaultedSingletonResponseType } from './preconfiguredMessagesTypes';

export const RAPIDSOS_OWNER = 'rapidsos';

/**
 * Whether a message is a rapidsos-owned system default (as opposed to one
 * customized by the authority).
 */
export const isSystemMessage = (message: PreconfiguredResponse) =>
  message.owner === RAPIDSOS_OWNER;

/**
 * Whether a message is authority-owned rather than a rapidsos system default.
 */
export const isAuthorityMessage = (message: PreconfiguredResponse) =>
  !isSystemMessage(message);

/**
 * Resolve the message configured for a singleton type, preferring the
 * authority-owned message and falling back to the rapidsos system default.
 *
 * These singleton types are always seeded with a rapidsos system default, so a
 * message can always be resolved. Throws if none is found — that means the
 * expected system default is missing (or the messages failed to load), which
 * callers otherwise hit as an opaque `undefined.body` crash downstream.
 */
export const getPreferredMessageByType = (
  messages: PreconfiguredResponse[],
  type: DefaultedSingletonResponseType,
): PreconfiguredResponse => {
  const matches = messages.filter(m => m.type === type);

  // Prefer the authority-owned message; fall back to the rapidsos default.
  const message =
    matches.find(isAuthorityMessage) ?? matches.find(isSystemMessage);

  if (!message) {
    throw new Error(`No preconfigured message found for type "${type}"`);
  }

  return message;
};

/**
 * Whether a message's body contains at least one of the given smart phrases.
 */
export const hasMatchingSmartPhrases = (
  message: PreconfiguredResponse,
  keys: SmartPhraseKey[],
) => {
  const phrases = parseSmartPhrases(message.body);

  return phrases.some(
    phrase =>
      isPhraseSegment(phrase) &&
      keys.includes((phrase.phrase?.key ?? '') as (typeof keys)[number]),
  );
};

/**
 * Whether a message's body contains any smart phrase.
 */
export const hasSmartPhrases = (message: PreconfiguredResponse) =>
  parseSmartPhrases(message.body).some(isPhraseSegment);

/**
 * Whether a message names a common emergency number (911, 999 or 112). Used to
 * warn, not block, when an agency's bounce-back does not tell the texter what
 * to call.
 */
export const namesEmergencyNumber = (message: PreconfiguredResponse) =>
  /(^|\D)(911|999|112)(\D|$)/.test(message.body);

/**
 * Whether a message references a smart phrase whose required capability is not
 * active for the current authority.
 */
export const hasDisabledSmartPhrases = (
  message: PreconfiguredResponse,
  activeCapabilities: Record<string, ActiveCapability>,
): boolean =>
  parseSmartPhrases(message.body).some(
    segment =>
      isPhraseSegment(segment) &&
      segment.phrase?.capability &&
      !activeCapabilities[segment.phrase.capability],
  );

/**
 * Whether a message references a smart phrase that requires the caller's
 * location (e.g. a location request link).
 */
export const messageRequiresLocation = (msg: PreconfiguredResponse) => {
  return hasMatchingSmartPhrases(msg, ['LocationRequestLink']);
};

/**
 * Whether a message references a smart phrase that requires roadside
 * assistance.
 */
export const messageRequiresRoadside = (msg: PreconfiguredResponse) => {
  return hasMatchingSmartPhrases(msg, ['RoadsideAssistanceLink']);
};

/**
 * Whether a message references a smart phrase that requires the caller's ID,
 * i.e. any link sent to the caller (location or roadside assistance).
 */
export const messageRequiresCallerId = (msg: PreconfiguredResponse) => {
  return hasMatchingSmartPhrases(msg, [
    'LocationRequestLink',
    'RoadsideAssistanceLink',
  ]);
};

/**
 * A preconfigured message whose smart phrases have been interpolated against a
 * context, carrying the resolved body plus the individual resolved phrases.
 */
export type ResolvedPreconfiguredResponse = PreconfiguredResponse &
  Awaited<ReturnType<typeof interpolateSmartPhrasesWithResolvedPhrases>>;

/**
 * Return a copy of a message with its smart phrases interpolated against the
 * given context, resolving any dynamic (async) phrase values in the body.
 */
export const handleDynamicMessage = async (
  preconfiguredMessage: PreconfiguredResponse,
  context: SmartPhraseContext,
): Promise<ResolvedPreconfiguredResponse> => {
  const smartPhraseData = await interpolateSmartPhrasesWithResolvedPhrases(
    preconfiguredMessage.body,
    context,
  );

  return {
    ...preconfiguredMessage,
    ...smartPhraseData,
  };
};
